import React, { useMemo, useState } from "react";

export default function ScenarioCoverageManager({
  coverage,
  scenarios = [],
  loading,
  onClose,
  onLink,
  onUnlink,
}) {
  const [scenarioId, setScenarioId] = useState("");
  const [priorityOrder, setPriorityOrder] = useState(1);
  const [isPrimary, setIsPrimary] = useState(false);
  const [scenarioLimit, setScenarioLimit] = useState("");
  const [scenarioDeductible, setScenarioDeductible] = useState("");

  const linkedScenarioIds = useMemo(
    () =>
      new Set(
        (coverage?.linked_scenarios || []).map(
          (item) => String(item.scenario_id)
        )
      ),
    [coverage]
  );

  const availableScenarios = scenarios.filter(
    (scenario) =>
      !linkedScenarioIds.has(String(scenario.id))
  );

  const handleLink = async () => {
    if (!scenarioId) return;

    await onLink({
      scenario_id: Number(scenarioId),
      allocation_percentage: 1,
      scenario_limit:
        scenarioLimit === ""
          ? null
          : Number(scenarioLimit),
      scenario_deductible:
        scenarioDeductible === ""
          ? null
          : Number(scenarioDeductible),
      priority_order: Number(priorityOrder),
      is_primary: isPrimary,
      is_active: true,
    });

    setScenarioId("");
    setPriorityOrder(1);
    setIsPrimary(false);
    setScenarioLimit("");
    setScenarioDeductible("");
  };

  if (!coverage) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Manage Scenarios
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {coverage.name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            Close
          </button>
        </div>

        <section className="mt-6">
          <h3 className="text-sm font-semibold text-slate-900">
            Linked Scenarios
          </h3>

          <div className="mt-3 space-y-3">
            {(coverage.linked_scenarios || []).length === 0 ? (
              <p className="text-sm text-slate-500">
                No scenarios are linked to this coverage.
              </p>
            ) : (
              coverage.linked_scenarios.map((item) => (
                <div
                  key={item.scenario_coverage_id}
                  className="rounded-lg border border-slate-200 p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-slate-900">
                        {item.scenario_name}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        {item.asset_name || "No asset assigned"}
                      </p>

                      <p className="mt-2 text-xs text-slate-500">
                        Priority {item.priority_order}
                        {item.is_primary ? " · Primary" : ""}
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled={loading}
                      onClick={() =>
                        onUnlink(item.scenario_id)
                      }
                      className="text-sm font-medium text-red-700 disabled:opacity-50"
                    >
                      Unlink
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-900">
            Link New Scenario
          </h3>

          <div className="mt-4 space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-500">
                FAIR Scenario
              </span>

              <select
                value={scenarioId}
                onChange={(event) =>
                  setScenarioId(event.target.value)
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">
                  Select a scenario
                </option>

                {availableScenarios.map((scenario) => (
                  <option
                    key={scenario.id}
                    value={scenario.id}
                  >
                    {scenario.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <NumberField
                label="Priority Order"
                value={priorityOrder}
                min="1"
                step="1"
                onChange={setPriorityOrder}
              />

              <label className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={isPrimary}
                  onChange={(event) =>
                    setIsPrimary(event.target.checked)
                  }
                />

                <span className="text-sm text-slate-700">
                  Primary coverage
                </span>
              </label>

              <NumberField
                label="Scenario Limit Override"
                value={scenarioLimit}
                min="0"
                step="10000"
                onChange={setScenarioLimit}
                placeholder="Use coverage limit"
              />

              <NumberField
                label="Scenario Deductible Override"
                value={scenarioDeductible}
                min="0"
                step="10000"
                onChange={setScenarioDeductible}
                placeholder="Use coverage deductible"
              />
            </div>

            <button
              type="button"
              disabled={!scenarioId || loading}
              onClick={handleLink}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Saving..." : "Link Scenario"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  step,
  placeholder,
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <input
        type="number"
        value={value}
        min={min}
        step={step}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </label>
  );
}